import React from "react";
import { Plus, BookOpen, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";

export default function EmptyPortfolioState({ onAdd, onLearnMore }) {
  const { t } = useTranslation();
  // Phase 8.LP.LR v1.7 — Replaced the previous "AAPL · 10 shares ·
  // Buy $150 · Fair Value $178.50 · Score 74 · Low Risk" sample rows
  // with a small, non-data feature list. New Premium users MUST NOT
  // see fabricated tickers, share counts, prices, or scores in the
  // empty state — that risks being read as real portfolio data and
  // undermines trust in every downstream number the app shows.
  const features = [
    t("portfolio.empty.feature_fair_value"),
    t("portfolio.empty.feature_upside"),
    t("portfolio.empty.feature_risk"),
  ];
  return (
    <div
      data-testid="portfolio-empty-state"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-card/60 backdrop-blur-xl p-8 sm:p-12 text-center"
    >
      <div className="absolute top-0 left-8 right-8 h-[2px] rounded-full bg-[#3DEBFF] shadow-[0_0_12px_rgba(61,235,255,0.45)]" />
      <div className="max-w-md mx-auto">
        <div className="font-display text-2xl sm:text-3xl tracking-tight mb-2">{t("portfolio.empty.title")}</div>
        <p className="text-sm text-muted-foreground mb-6">{t("portfolio.empty.body")}</p>
        <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
          <Button onClick={onAdd} data-testid="portfolio-empty-add-holding-button" className="shadow-[0_0_0_1.5px_rgba(61,235,255,0.55)]">
            <Plus className="w-4 h-4 mr-1.5" /> {t("portfolio.empty.cta")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onLearnMore}
            data-testid="portfolio-empty-learn-more"
          >
            <BookOpen className="w-3.5 h-3.5 mr-1.5" /> {t("portfolio.empty.learn_more")}
          </Button>
        </div>
        {/* Non-data feature list — replaces the previous fake AAPL /
            MSFT sample rows. No tickers, no prices, no scores. Just
            three plain-language capability lines so the empty state
            doesn't feel too sparse after the sample block was
            removed. */}
        <ul
          data-testid="portfolio-empty-feature-list"
          className="mt-6 mx-auto max-w-md text-left text-sm text-muted-foreground/90 space-y-2"
        >
          {features.map((label, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <Check
                aria-hidden
                className="w-4 h-4 mt-0.5 shrink-0 text-[#3DEBFF]"
              />
              <span>{label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
